import React from 'react';
import {
  Wrap,
  Product,
  Typography,
  SkeletonLoader,
  LocationDropdown,
  NothingToDisplay,
  Button,
} from '../../components/index';
import { FreeProductsReducer } from '../../interfaces';
import * as _ from 'lodash';
import { Row, Col } from 'react-styled-flexboxgrid';
import {
  useResponsivity,
  renderCdnUrl,
  scrollToTop,
} from '../../helpers/helpers';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import store from 'store2';
import Styled from './FreeProducts.style';

export interface FreeProductsProps {
  page: FreeProductsReducer;
  getFreeProducts: Function;
  addToOrder: Function;
  removeFromOrder: Function;
  getAddresses: Function;
  listLocation: any;
  user: any;
  getPropertyDetails: Function;
  setMeta: Function;
  getFreeProductsPack: Function;
  addAllToOrder: Function;
}

const FreeProducts: React.FC<FreeProductsProps> = (props) => {
  const {
    page,
    getFreeProducts,
    addAllToOrder,
    removeFromOrder,
    getAddresses,
    listLocation,
    getPropertyDetails,
    user,
    setMeta,
    getFreeProductsPack,
  } = props;

  const navigate = useNavigate();
  const isMobile = useResponsivity();
  const [t] = useTranslation('common');

  const dataLocation = _.get(listLocation, 'data.data');
  const dataLength = _.get(dataLocation, 'length');
  const initialPropertyId = Number(store.get('property')) || 0;
  const data = _.get(page, 'products.data.products');

  const [propertyId, setPropertyId] = React.useState<number>(initialPropertyId);

  const [isChangingLocation, setIsChangingLocation] =
    React.useState<boolean>(true);

  const [productsPropertyId, setProductsPropertyId] = React.useState<
    number | null
  >(null);

  const findLocation = _.find(dataLocation, (location) => {
    return location.id === propertyId;
  });

  const postcode = findLocation?.postcode;

  const wholesalerSlug = _.get(user, 'data.data.wholesaler_slug');

  const loadedPropertyId = _.get(user, 'data.data.property_id');

  const {
    error = false,
    loading = false,
    done = false,
  } = _.get(page, 'products.status');

  const renderMapOptions =
    dataLocation &&
    dataLocation.map((el) => ({
      value: el.id,
      label: el.property_name,
    }));

  React.useEffect(() => {
    if (!propertyId) {
      return;
    }

    getPropertyDetails(propertyId);
  }, [propertyId, getPropertyDetails]);

  React.useEffect(() => {
    if (!propertyId) {
      return;
    }

    if (!loadedPropertyId || loadedPropertyId !== propertyId) {
      return;
    }

    if (!wholesalerSlug) {
      setProductsPropertyId(null);
      setIsChangingLocation(false);
      return;
    }

    setProductsPropertyId(propertyId);

    // getFreeProducts(wholesalerSlug);
    getFreeProductsPack(propertyId);

    if (postcode) {
      getAddresses(postcode);
    }
  }, [
    propertyId,
    loadedPropertyId,
    wholesalerSlug,
    postcode,
    getFreeProducts,
    getAddresses,
  ]);

  React.useEffect(() => {
    if (!isChangingLocation) {
      return;
    }

    if (productsPropertyId !== propertyId) {
      return;
    }

    if (loading) {
      return;
    }

    if (done || error) {
      setIsChangingLocation(false);
    }
  }, [
    loading,
    done,
    error,
    propertyId,
    productsPropertyId,
    isChangingLocation,
  ]);

  const pageTitle = t('portal.free_products.page_title');

  const pageDisclaimer = t('portal.free_products.page_disclaimer');

  const renderProducts = () => {
    return (
      <Styled.ProductsGrid>
        {data.map((el, index) => {
          const hasImage = _.get(el, 'images.path');
          const image = hasImage
            ? `https://cdn.savewatersavemoney.co.uk/cache/product_page${hasImage}`
            : renderCdnUrl('files/gwf/no-image.png');

          return (
            <Styled.ProductCard key={index}>
              <Styled.ProductImageWrapper>
                <Styled.ProductImage src={image} alt={el.title} />
              </Styled.ProductImageWrapper>

              <Styled.ProductContent>
                <Styled.ProductTitle>{el.title}</Styled.ProductTitle>

                {el.freePackDescription && (
                  <Styled.ProductDescription>
                    {el.freePackDescription}
                  </Styled.ProductDescription>
                )}

                <Styled.ProductQuantity>
                  <Styled.QuantityLabel>Quantity</Styled.QuantityLabel>
                  <Styled.QuantityValue>
                    {el.maxperhousehold}
                  </Styled.QuantityValue>
                </Styled.ProductQuantity>
              </Styled.ProductContent>
            </Styled.ProductCard>
          );
        })}
      </Styled.ProductsGrid>
    );
  };
  const renderContent = () => {
    if (!wholesalerSlug) {
      return (
        <Row>
          <Col md={12} xs={12} sm={12}>
            <NothingToDisplay
              title="There are no products available"
              subtitle="Please come back later to check if something has changed"
            />
          </Col>
        </Row>
      );
    }

    if (error || data.length === 0) {
      return (
        <Row>
          <Col md={12} xs={12} sm={12}>
            <NothingToDisplay
              title="There are no products available"
              subtitle="Please come back later to check if something has changed"
            />
          </Col>
        </Row>
      );
    }

    return (
      <Row>
        <Col md={12} sm={12} xs={12}>
          {renderProducts()}
        </Col>

        <Col md={12} sm={12} xs={12}>
          <Styled.CheckoutSection>
            <Styled.CheckoutDisclaimer>
              <Typography size="p" text={pageDisclaimer} />
            </Styled.CheckoutDisclaimer>

            <Styled.CheckoutButton
              onClick={() => {
                store('property', propertyId);
                scrollToTop();
                addAllToOrder(data);
                navigate('/free-products/checkout');
              }}
            >
              {t('portal.free_products.page_checkout')}
            </Styled.CheckoutButton>
          </Styled.CheckoutSection>
        </Col>
      </Row>
    );
  };

  const shouldShowSkeleton = isChangingLocation || (loading && !done);

  return (
    <Wrap>
      <Row>
        <Col md={8} xs={12} sm={12}>
          <Typography
            weight={700}
            size="1"
            text={pageTitle}
            style={{
              marginBottom: '16px',
              fontSize: !isMobile ? '40px' : '28px',
              color: '#0b2641',
            }}
          />
          {data && data.length > 0 && (
            <Typography
              size="p"
              text="One order per business premises, so make sure you have audited all washrooms and kitchens before placing your order."
            />
          )}
        </Col>

        {dataLength <= 1 ? null : (
          <Col md={4} xs={12} sm={12}>
            <Styled.DropdownWrap>
              <LocationDropdown
                value={String(propertyId)}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setIsChangingLocation(true);
                  setProductsPropertyId(null);
                  setPropertyId(id);
                  store('property', id);
                  setMeta(id);
                }}
                options={renderMapOptions}
              />
            </Styled.DropdownWrap>
          </Col>
        )}

        <Col md={12} xs={12} sm={12}>
          {shouldShowSkeleton ? <SkeletonLoader /> : renderContent()}
        </Col>
      </Row>
    </Wrap>
  );
};

export default FreeProducts;
